import { z } from 'zod';
import { assertTypes, type NoNewFields } from '../primitives';

/**
 * Zest Billing: what Zest venues (restaurants) pay OrderHub for through Stripe, on Zest's own
 * billing account, separate from its Connect account. Zest plans are free (`restaurantPlanSchema.ts`),
 * so Zest venues pay only for SMS packs and their custom domain.
 *
 * - Who: any of the venue's users, since roles aren't enforced yet. Only Zest venues; every other
 *   brand is refused (409).
 * - One Stripe customer per venue (`userId` is the venue's `restaurantId`), without an email, so it
 *   never matches another venue's customer.
 * - Prices live in Stripe, never here, found by their `lookup_key`. They're in AUD, including GST:
 *   every purchase gets the one 10% AU GST tax rate, which is inclusive.
 * - SMS packs: one-off Checkout payments (`billing.zest.buySmsPack`). An SMS credit covers the
 *   customer SMS of one online order.
 *   - Each pack adds `SMS_PACK.orderCount` credits once it's paid (`billing/zest/_syncCheckoutSession.ts`).
 *     Stripe can deliver a payment twice, so the row keeps the Checkout sessions it was credited for.
 *   - A Zest venue's order uses one credit at its first SMS: when it's accepted, or cancelled before
 *     being accepted (`spendSmsCredit`). A cancellation after it was accepted is covered. With none
 *     left, the order still goes through and the customer gets no SMS. Every other brand's SMS are
 *     free.
 *   - `smsCredits` holds one row per venue that ever bought a pack. No row is no credits.
 * - Custom domain: a monthly subscription (`billing.zest.subscribeCustomDomain`), whose one item is
 *   the `CUSTOM_DOMAIN_PRICE_LOOKUP_KEY` price.
 *   - The webhook mirrors it onto the venue (`billing/zest/_syncCustomDomainSubscription.ts`): while
 *     it's `active`, `trialing` or `past_due`, the venue has a `customDomainSubscriptions` row and its
 *     `custom-domain` flag is on. After it ends, or in any other status, both go.
 *   - OrderHub sets the domain up by hand (Website config, Vercel) while the flag is on, and moves the
 *     venue back to its Zest site after it turns off.
 * - The Customer Portal handles cards, invoices and cancelling at the end of the period.
 */

/** The SMS pack Zest venues buy: its price's lookup key, and the orders it adds SMS for. */
export const SMS_PACK = { lookupKey: 'sms-pack-250', orderCount: 250 } as const;

/** The lookup key of the custom domain's monthly price. */
export const CUSTOM_DOMAIN_PRICE_LOOKUP_KEY = 'custom-domain';

/** A Zest venue's SMS credits. See the rules above. */
export const smsCreditSchema = z.object({
  restaurantId: z.string(),
  /** Orders the venue has customer SMS left for. */
  balance: z.number().int().min(0),
  /** The Checkout sessions on the Zest Billing account whose SMS packs added to `balance`. */
  stripeCheckoutSessionIds: z.array(z.string()),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type SmsCredit = z.output<typeof smsCreditSchema>;

/** Strict schema */
export const smsCreditStrictSchema = smsCreditSchema;

/** Convex schema */
export const smsCreditCvxSchema = smsCreditSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type SmsCreditCvx = z.output<typeof smsCreditCvxSchema>;
assertTypes<NoNewFields<SmsCredit, SmsCreditCvx>>();

/** A Zest venue's live custom domain subscription. See the rules above. */
export const customDomainSubscriptionSchema = z.object({
  restaurantId: z.string(),
  /** The subscription on the Zest Billing account. */
  stripeSubscriptionId: z.string(),

  _creationTime: z.number().optional(),
  _id: z.string().optional(),
});
export type CustomDomainSubscription = z.output<typeof customDomainSubscriptionSchema>;

/** Strict schema */
export const customDomainSubscriptionStrictSchema = customDomainSubscriptionSchema;

/** Convex schema */
export const customDomainSubscriptionCvxSchema = customDomainSubscriptionSchema.safeExtend({
  _creationTime: z.number(),
  _id: z.string(),
});
export type CustomDomainSubscriptionCvx = z.output<typeof customDomainSubscriptionCvxSchema>;
assertTypes<NoNewFields<CustomDomainSubscription, CustomDomainSubscriptionCvx>>();
